/**
 * Summary view component.
 * @module components/theme/View/SummaryView
 */

import React from 'react';
import PropTypes from 'prop-types';
import { UniversalLink } from '@plone/volto/components';
import { Container as SemanticContainer } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import config from '@plone/volto/registry';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Pagination } from '@plone/volto/components';

/**
 * Summary view component class.
 * @function SummaryView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const SummaryView = ({ content }) => {
  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;
  const PreviewImage = config.getComponent({ name: 'PreviewImage' }).component;

  const dispatch = useDispatch();
  const items = useSelector((state) => state.search.subrequests.items?.items);
  const total = useSelector((state) => state.search.subrequests.items?.total);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(30);

  useEffect(() => {
    let contentFilter = {
      b_size: pageSize,
      b_start: currentPage * pageSize,
      sort_on: 'getObjPositionInParent',
      metadata_fields: ['content_icon', 'getRemoteUrl'],
    };
    contentFilter['path.depth'] = 1
    dispatch(searchContent(flattenToAppURL(content['@id']), contentFilter, 'items'));
  }, [dispatch, currentPage, pageSize]);

  const onChangePage = (event, activePage) => {
    event.preventDefault();
    setCurrentPage(activePage.value);
  };

  const onChangePageSize = (event) => {
    event.preventDefault();
    setPageSize(parseInt(event.target.text));
    setCurrentPage(0);
  };

  const getViewURL = function (item) {
    let URL = flattenToAppURL(item['@id'])
    if (item['@type'] === 'File') {
      URL += '/@@display-file/file'
    }
    if (item['@type'] === 'Image') {
      URL += '/@@display-file/image'
    }
    if (item?.getRemoteUrl) {
      URL = item?.getRemoteUrl
    }
    return URL
  }

  return (
    <div className="view-wrapper summary-view">
      <div id="content">
        <ul id="content-core" className='list-unstyled'>
          {items &&
            items.map((item, index) => (
              <li key={item['@id']}>
                <article  className='summaryItem'>
                  {item.image_field && (
                    <PreviewImage
                      item={item}
                      alt=''
                      className="ui image floated right clear"
                      responsive={true}
                      loading="lazy"
                    />
                  )}
                  <div className="summaryItem__text">
                    <h2 className='summaryItem__title'>{item.title}</h2>
                    {item.description && <p className='summaryItem__desc'>{item.description}</p>}
                    <p className='summaryItem__readMore'>
                      <UniversalLink href={getViewURL(item)}>
                        <FormattedMessage
                          id="Read More…"
                          defaultMessage="Read More…"
                        />
                        <span className="visually-hidden">{item.title}</span>
                      </UniversalLink>
                    </p>
                  </div>
                  
                </article>
              </li>
            ))}

          {total > pageSize && (
            <nav className="pagination">
              <Pagination
                current={currentPage}
                total={Math.ceil(total / pageSize)}
                onChangePage={onChangePage}
                onChangePageSize={onChangePageSize}
              />
            </nav>
          )}

        </ul>
      </div>
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
SummaryView.propTypes = {
  /**
   * Content of the object
   */
  content: PropTypes.shape({
    /**
     * Title of the object
     */
    title: PropTypes.string,
    /**
     * Description of the object
     */
    description: PropTypes.string,
    /**
     * Child items of the object
     */
    items: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Title of the item
         */
        title: PropTypes.string,
        /**
         * Description of the item
         */
        description: PropTypes.string,
        /**
         * Url of the item
         */
        url: PropTypes.string,
        /**
         * Image of the item
         */
        image: PropTypes.object,
        /**
         * Image caption of the item
         */
        image_caption: PropTypes.string,
        /**
         * Type of the item
         */
        '@type': PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default SummaryView;
